import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Footer from "../../components/footer";
import BackToTopButton from "../../components/back-to-top/component";
import Header from "../../components/header";
import Loader from "../../components/loader";
import {
  CONTENT_KEY_BIG_FIVE,
  MODEL_ROMANTIC_PERSONALIZED,
  PERSONALITY_TEST_ASSESSMENT_ID,
  ROMANTIC_COMPATIBILITY_TRAIT_KEY_MAP,
} from "../../utils/general-config";

import { romanticCompatibilityPersonalizedLevelSuffix } from "../../utils/assessment-utils";
import ExpandableSection from "../../components/expandable-collapsible/component";
import {
  getEnneagramBannerImage,
  getRomanticCompatibilityImage,
  InvitationStatus,
} from "../../utils/utility-functions";
import { useTranslation } from "react-i18next";
import { CreateNewUserModel } from "../../components/model/component";
import { APP_NAVIGATION, LOCALE } from "../../utils/constants";
import PathHelper from "../../utils/path-helper";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import { createCookie, readCookie } from "../../utils/helpers";

const RomanticPersonalizedReport = ({
  funnelStep,
  getInsightfulContentConnect,
  getUserByIdConnect,
  getAssessmentResultByAssessmentAttemptIdConnect,
  sendToFriendReportOnEmailConnect,
  sendToPartnerReportOnEmailConnect,
  getGuestUserConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const [userTraits, setUserTraits] = useState([]);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState({});
  // const token = getCookie("jwtToken");
  const { userGUID, isAuth, maxSubUserCount } = funnelStep || {};
  const { locale } = useParams();
  const currentLocale = locale || LOCALE.EN;
  const [contentData, setContentData] = useState({});
  const assessmentAttemptId = queryParams.get("aai");
  const { t } = useTranslation();
  const [selectedAssessmentMapId, setSelectedAssessmentMapId] = useState("");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const [selectedAssessmentAttemptId, setSelectedAssessmentAttemptId] =
    useState("");
  const [isPremiumOrGuest, setIsPremiumOrGuest] = useState(false);
  const userId = queryParams.get("uid") || userGUID;
  const [guestUserData, setGuestUserData] = useState([]);
  const [userData, setUserData] = useState(null);
  const [openSendToPartner, setOpenSendToPartner] = useState(false);
  const [testPanelOpen, setTestPanelOpen] = useState(null);
  const [popoverOpen, setPopoverOpen] = React.useState({});
  const [assessmentAttemptData, setAssessmentAttemptData] = useState([]);
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);
  const [guestUserCount, setGuestUserCount] = useState(0);
  const relationshipstyle = "relationship-personalized";
  const compatibilityImage = getRomanticCompatibilityImage(relationshipstyle);
  //const guestUserCount = readCookie("guestUserCount");
  //const maxSubUserCount = readCookie("maxSubUserCount");

  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];

  const DATA =
    assessmentAttemptData?.length > 0
      ? assessmentAttemptData
          .map((item) => {
            return {
              assessmentId: item?.assessmentId,
              assessmentAttemptId: item?.assessmentAttemptId,
              assessmentMapId: item?.assessmentMapId,
              reportId: item?.assessmentAttemptId,
              reportTitle: item?.assessmentTitle || "",
              reportDate: item?.createdOn,
              personalityType: item?.personality || "",
              assessmentMapId: item?.assessmentMapId,
              localeCode: item?.nameEnglishCode,

              score: item?.score || null,
              detailsUrl: `/report/${item?.assessmentAttemptId}`,
            };
          })
          .sort((a, b) => new Date(b.reportDate) - new Date(a.reportDate))
      : [];

  const selectedAssessment = DATA.find(
    (item) => item.assessmentAttemptId === testPanelOpen,
  );

  useEffect(() => {
    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits

        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );

        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData({
            ...traitsResponse,
            assessmentAttemptId: assessmentAttemptId,
          });
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_ROMANTIC_PERSONALIZED[currentLocale] ||
            MODEL_ROMANTIC_PERSONALIZED[LOCALE.EN],
          contentKey: CONTENT_KEY_BIG_FIVE,
        });

        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [userGUID, assessmentAttemptId, isAuth]);

  useEffect(() => {
    if (userId) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
      isGuestOrPremiumUserConnect(userId).then((res) => {
        if (res) {
          const flag = res?.isPremiumOrGuestUser;
          setIsPremiumOrGuest(flag);
        }
      });
      getGuestUserConnect(userGUID).then((res) => {
        if (res) {
          const filteredData = res.filter(
            (item) =>
              item.invitationStatus === InvitationStatus.Sent ||
              item?.invitationStatus === InvitationStatus.Accepted,
          );
          const nonRemovedInvitations = res.filter(
            (item) => item.invitationStatus !== InvitationStatus.Removed,
          );
          setGuestUserData(nonRemovedInvitations);
          setGuestUserCount(filteredData.length);
          // createCookie("guestUserCount", filteredData.length, 1);
        }
      });
    }
  }, [userId]);

  const handleSendToPartner = (item) => {
    setPopoverOpen({});
    setTestPanelOpen(null);
    setOpenSendToPartner(true);
    setSelectedAssessmentId(item?.assessmentId);
    setSelectedAssessmentAttemptId(item?.assessmentAttemptId);
    setSelectedAssessmentMapId(item?.assessmentMapId);
  };

  const bannerImage = getEnneagramBannerImage();

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };
  const HeroBanner = ({ title, color = "purple" }) => {
    const gradientClasses = colorVariants[color] || colorVariants.purple;

    if (!title) return null;
    const isString = typeof title === "string";
    let firstWord = "";
    let restOfTitle = "";
    if (isString) {
      const words = title.split(" ");
      firstWord = words[0];
      restOfTitle = words.slice(1).join(" ");
    }

    return (
      <div
        className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
      >
        <img
          src={bannerImage}
          alt={title}
          className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
        />

        <div
          className={`absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent z-5`}
        />
        <div className="absolute inset-0 bg-black opacity-10 z-5" />

        <div className="relative h-full flex items-start" id={reportType}>
          <div className="w-full px-4 py-8 lg:px-8 lg:py-12" id={reportType}>
            <div className="flex flex-col md:flex-row justify-between gap-4 items-start pr-10 md:pr-16">
              <h2
                id={reportType}
                style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                className="text-3xl font-bold text-white text-left break-words whitespace-normal"
              >
                {isString ? (
                  <span className="inline-block max-w-full md:max-w-none leading-tight">
                    <span className="block sm:inline">{firstWord}</span>
                    {restOfTitle && (
                      <>
                        <span className="hidden sm:inline"> </span>
                        <span className="block sm:inline">{restOfTitle}</span>
                      </>
                    )}
                  </span>
                ) : (
                  title
                )}
              </h2>
            </div>
          </div>
        </div>
      </div>
    );
  };
  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
  }) => {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 items-start mb-5">
        <div
          className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
        >
          {heading && (
            <div className="flex items-start gap-2 mb-2">
              <div>
                <div className="flex items-start gap-2">
                  {character && (
                    <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                      {" "}
                    </div>
                  )}

                  <h2
                    className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                  >
                    {heading}
                  </h2>
                </div>
              </div>
            </div>
          )}

          {overview && (
            <p
              className={`text-lg text-gray-700 mt-4 ${
                character ? "pl-8 md:pl-10" : ""
              } ${contentArray ? `mb-4` : ""}`}
            >
              {overview}
            </p>
          )}
          {contentArray && (
            <ul
              className={`space-y-2 mt-4 ${
                overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
              }`}
            >
              {contentArray?.map((item, index) => {
                return (
                  <li key={index} className="flex gap-4">
                    {/* Right Section: Heading & Content */}
                    <div className="flex flex-col">
                      {isSubHeading ? (
                        <>
                          {/* Heading and First Line in Same Row */}
                          <div className="flex items-center gap-2">
                            <h2 className="text-lg font-semibold text-gray-700">
                              {item?.heading}
                            </h2>
                          </div>

                          {/* Paragraph with text-indent effect */}
                          <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                            <span className="">{item.content}</span>
                          </p>
                        </>
                      ) : (
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                          {item}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {isAuth && <div className="mt-6">{renderInviteYourPartner()}</div>}
        </div>
        {/* Right Section - Image */}
        <div className="p-4 md:p-6 flex flex-col break-words overflow-hidden">
          <img
            src={compatibilityImage}
            alt="Image Here"
            className="w-full object-contain"
          />
        </div>
      </div>
    );
  };
  const ProgressCircle = ({ color = "#2563EB", targetPercentage = 50 }) => {
    const radius = 50;
    const circumference = 2 * Math.PI * radius;

    // Directly set percentage (no animation)
    const percentage = targetPercentage;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div className="w-full max-w-xs p-4 mx-auto">
        <div className="relative pb-[100%]">
          <svg
            className="absolute top-0 left-0 w-full h-full transform -rotate-90"
            viewBox="0 0 120 120"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke="#E5E7EB"
              strokeWidth="10"
              fill="none"
            />
            {/* Progress Circle */}
            <circle
              cx="60"
              cy="60"
              r={radius}
              stroke={color}
              strokeWidth="10"
              fill="none"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
            />
          </svg>

          {/* Center Content */}
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="150"
              height="150"
              viewBox="0 0 64 64"
            >
              <circle cx="32" cy="32" r="30" fill="white" />
              <text
                x="32"
                y="46"
                textAnchor="middle"
                fontSize="48"
                fontFamily="Arial, Helvetica, sans-serif"
                fontWeight="700"
                fill="#2563EB"
              >
                ?
              </text>
            </svg>
          </div>
        </div>
      </div>
    );
  };
  const renderInviteYourPartner = () => {
    // Create assessment object from current data
    const currentAssessment = {
      assessmentId: currentAssessmentAttemptData?.assessmentId,
      assessmentAttemptId: assessmentAttemptId,
      assessmentMapId: currentAssessmentAttemptData?.assessmentMapId,
    };

    return (
      <div className="mt-5 w-full">
        <button
          onClick={() => {
            handleSendToPartner(currentAssessment);
          }}
          className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 text-white transition-colors py-3 rounded-3xl px-6 sm:px-8 text-base sm:text-lg font-bold uppercase tracking-wider flex items-center justify-center sm:justify-start sm:gap-3"
        >
          {t("report.romantic-personalized.partner_invite")}
          {/* Show icon only on sm+ */}
          <span className="hidden sm:inline-flex">
            <svg
              className="fill-white"
              width="24"
              height="24"
              xmlns="http://www.w3.org/2000/svg"
              fillRule="evenodd"
              clipRule="evenodd"
              viewBox="0 0 24 24"
            >
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 2v.01L12 13 4 6.01V6h16zM4 18V8l8 5 8-5v10H4z" />
            </svg>
          </span>
        </button>
      </div>
    );
  };
  const renderCompatibilityCards = () => {
    return userTraits.map((traitObj) => {
      const traitTitle = traitObj.trait.title; // Get the trait title from the nested structure
      const percentage = traitObj.percentage;

      // TRAIT_KEY_MAP to match your data structure

      const baseKey = ROMANTIC_COMPATIBILITY_TRAIT_KEY_MAP[traitTitle];
      if (!baseKey) {
        console.warn(`No trait mapping: ${traitTitle}`);
        return null;
      }

      const suffix = romanticCompatibilityPersonalizedLevelSuffix(percentage);
      const keyWithSuffix = `${baseKey}_${suffix}`;

      // Access the content object from your JSON structure
      const traitData = contentData?.[keyWithSuffix] || {};

      const fullName = traitData.FullName || `${traitTitle}`;
      const scoreExplanation = traitData.ScoreExplanation || "";
      const immediateFocusAreas = traitData.ImmediateFocusAreas || [];
      const longTermGoals = traitData.LongTermDevelopmentGoals || [];
      const howToCommunicate = traitData.HowToCommunicate || "";

      // Get headings from the root content level
      const immediateFocusHeading =
        contentData?.ImmediateFocusHeading ||
        "Immediate Focus Areas (Next 30 Days)";
      const longTermGoalsHeading =
        contentData?.LongTermGoalsHeading ||
        "Long-term Development Goals (3-6 Months)";
      const howToCommunicateHeading =
        contentData?.HowToCommunicateHeading ||
        "How to Communicate this to Your Partner";

      const bgColorByLevel = {
        H: "#FFE5F0",
        M: "#FFF3E0",
        L: "#FFFCE0",
      };

      const textColorByLevel = {
        H: "#d22f65ff", // darker pink
        M: "#B85C00", // darker orange
        L: "#B89B00", // darker yellow
      };

      return (
        <div
          key={traitObj.id}
          style={{ backgroundColor: bgColorByLevel[suffix] }}
          className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col h-full w-full break-words overflow-hidden"
        >
          {/* Full Name */}
          <h2
            className="text-lg sm:text-xl font-semibold flex items-center mb-2 break-words min-w-0 pr-3 sm:pr-0" // 🔹 added break-words & min-w-0
            style={{ color: textColorByLevel[suffix] }}
          >
            {fullName}
          </h2>

          {/* Score Explanation */}
          {scoreExplanation && (
            <div className="mb-4">
              <p className="text-gray-700 text-lg mt-4 md:text-lg leading-relaxed relative">
                {scoreExplanation}
              </p>
            </div>
          )}

          {/* Immediate Focus Areas */}
          {immediateFocusAreas.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg sm:text-xl font-semibold flex items-center text-gray-700 mb-2">
                {immediateFocusHeading}
              </h3>
              <div className="space-y-3 mt-auto pr-3 sm:pr-0">
                {immediateFocusAreas.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 leading-relaxed"
                  >
                    {/* Custom SVG Icon */}
                    <div className="w-6 h-6 flex-shrink-0">
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <filter
                            id="shadow"
                            x="-20%"
                            y="-20%"
                            width="140%"
                            height="140%"
                          >
                            <feDropShadow
                              dx="0"
                              dy="2"
                              stdDeviation="3"
                              floodColor="black"
                              floodOpacity="0.4"
                            />
                          </filter>
                        </defs>
                        <path
                          d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z"
                          fill="#346bcc"
                          stroke="#fff"
                          strokeWidth="6"
                          filter="url(#shadow)"
                        />
                      </svg>
                    </div>
                    {/* Content */}
                    {/* 🔹 Added min-w-0 so text can shrink inside flex */}
                    <span className="break-words min-w-0">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Long Term Development Goals */}
          {longTermGoals.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg sm:text-xl font-semibold flex items-center text-gray-700 mb-2">
                {longTermGoalsHeading}
              </h3>
              <div className="space-y-2 mb-4 pl-1 pr-3 sm:pr-0">
                {longTermGoals.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 leading-relaxed"
                  >
                    {/* Custom SVG Icon */}
                    <div className="w-6 h-6 flex-shrink-0">
                      <svg
                        width="100%"
                        height="100%"
                        viewBox="0 0 48 48"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <filter
                            id="shadow"
                            x="-20%"
                            y="-20%"
                            width="140%"
                            height="140%"
                          >
                            <feDropShadow
                              dx="0"
                              dy="2"
                              stdDeviation="3"
                              floodColor="black"
                              floodOpacity="0.4"
                            />
                          </filter>
                        </defs>
                        <path
                          d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z"
                          fill="#346bcc"
                          stroke="#fff"
                          strokeWidth="6"
                          filter="url(#shadow)"
                        />
                      </svg>
                    </div>
                    {/* Content */}
                    {/* 🔹 Added min-w-0 */}
                    <span className="text-gray-700 text-base md:text-lg leading-relaxed break-words min-w-0">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* How To Communicate */}
          {howToCommunicate && (
            <div className="mt-auto">
              <h3 className="text-lg sm:text-xl font-semibold  flex items-center text-gray-700 mb-2">
                {howToCommunicateHeading}
              </h3>
              <p className="text-gray-700 text-lg break-words max-w-full overflow-hidden leading-relaxed">
                {howToCommunicate}
              </p>
            </div>
          )}
        </div>
      );
    });
  };
  const CallToActionSection = ({ data }) => {
    return (
      <section className="">
        {/* <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {contentData.CTARelationshipTools}
            </h3>
            <p className="text-white opacity-90 text-center">
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ROMANTIC_COMPATIBILITY_ASSESSMENT_ID}&newt=true`
                );
              }}
            >
              {t("dashboard.take_test_cta")}
            </button>
          </div>
        </div> */}
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {contentData.CTAPersonalityType}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t(
                "report.romantic-personalized.discover_more_about_your_personality_type",
              )}
            </p>
            <button
              id="romantic-personalized-take-personality-test-cta"
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("dashboard.take_test_cta")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div>
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner
                      title={t("report.romantic-personalized.romantic_heading")}
                      color="blue"
                    />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[50%_50%] gap-4 items-start">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal">
                          {contentData?.Intro?.RootHeading}
                        </h2>
                        <h2 className="text-lg text-gray-600 mb-4 whitespace-pre-line">
                          {contentData?.Intro?.Content}
                        </h2>
                        {isAuth && (
                          <div className="mt-6 mb-6 w-full">
                            {renderInviteYourPartner()}
                          </div>
                        )}
                      </div>
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <h2 className="text-3xl font-semibold text-gray-700 mt-1 pb-4 break-words whitespace-normal text-center">
                          {t(
                            "report.romantic-personalized.overall_compatibility",
                          )}
                        </h2>

                        {/* Right Section: Circlr Score Graph */}
                        <div className="flex items-center h-[100%] justify-center">
                          <ProgressCircle
                            targetPercentage={100}
                            color="#2563EB"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.YourStyleHeading}
                      color="blue"
                    />
                  }
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-5">
                    {renderCompatibilityCards()}
                  </div>
                  <div>
                    <DescriptiveCard
                      heading={contentData?.DisclaimerHeading}
                      overview={contentData?.Disclaimer}
                      bgClr={"bg-green-50"}
                      textClr={"text-green-800"}
                    />
                  </div>
                </ExpandableSection>
              </section>

              <CallToActionSection data={contentData} />
            </>
          )}
        </div>
      </div>
      <Footer />
      <CreateNewUserModel
        isOpen={openSendToPartner}
        setOpen={setOpenSendToPartner}
        parentId={userGUID}
        CreateGuestUserConnect={sendToPartnerReportOnEmailConnect}
        parentEmail={userData?.email}
        guestUserData={guestUserData}
        assessmentAttemptId={selectedAssessmentAttemptId}
        assessmentId={selectedAssessmentId}
        type={"send_to_partner"}
        assessmentMapId={selectedAssessmentMapId}
        t={t}
        currentLocale={currentLocale}
        isPremiumOrGuest={isPremiumOrGuest}
        guestUserCount={guestUserCount}
        maxSubUserCount={maxSubUserCount}
      />
    </>
  );
};

export default RomanticPersonalizedReport;
